/**
 * Centralized Axios API Client with Interceptors
 * Handles Firebase authentication tokens and request/response logging
 */

import axios from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios';
import { getAccessToken, saveTokens, clearTokens } from '@/utils/storage';
import { parseApiError } from './errorHandler';
import { logger } from '@/utils/logger';
import { pushNetworkEntry, captureError } from '@/debug/debugStore';
import { getAnalyticsSessionId, getAnalyticsVisitorId } from '@/utils/analyticsIdentity';

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    metadata?: { startTime: number; requestId: string };
  }
}

// Track if a token refresh is in progress to prevent duplicate refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

// Get base URL from environment
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL

const getHeaderValue = (headers: unknown, key: string): string | null => {
  if (!headers || typeof headers !== 'object') return null;
  const normalizer = headers as { get?: (name: string) => unknown };
  const fromGetter = normalizer.get?.(key);
  if (typeof fromGetter === 'string') return fromGetter;

  const record = headers as Record<string, unknown>;
  const direct = record[key] ?? record[key.toLowerCase()];
  return typeof direct === 'string' ? direct : null;
};

const resolveRequestUrl = (baseURL: string | undefined, url: string | undefined): string | null => {
  if (!url) return null;
  try {
    return new URL(url, baseURL || window.location.origin).toString();
  } catch {
    return url;
  }
};

const estimateRequestBodySize = (config: InternalAxiosRequestConfig): number | null => {
  const data = config.data as unknown;
  if (!data) return null;
  if (data instanceof FormData) {
    let total = 0;
    data.forEach((value) => {
      if (value instanceof File) total += value.size;
      else total += value.length;
    });
    return total;
  }
  if (typeof data === 'string') return data.length;
  try { return JSON.stringify(data).length; } catch { return null; }
};

const parseApiErrorBody = (data: unknown): { code: string | null; message: string | null } => {
  if (!data || typeof data !== 'object') return { code: null, message: null };
  const maybeError = (data as { error?: { code?: unknown; message?: unknown } }).error;
  return {
    code: typeof maybeError?.code === 'string' ? maybeError.code : null,
    message: typeof maybeError?.message === 'string' ? maybeError.message : null,
  };
};

/**
 * Create and configure Axios instance
 */
const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  /**
   * Request Interceptor: Attach session ID header
   * Generates a UUID per browser tab (sessionStorage) for view event tracking.
   */
  client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    config.headers['x-session-id'] = getAnalyticsSessionId();
    config.headers['x-visitor-id'] = getAnalyticsVisitorId();
    return config;
  });

  /**
   * Request Interceptor: Attach Firebase JWT token
   */
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Skip token attachment for auth endpoints (authenticate, refresh)
      // These endpoints have their own token handling logic
      if (config.url?.endsWith('/auth/authenticate') || config.url?.endsWith('/auth/refresh')) {
        return config;
      }

      const token = getAccessToken();

      // Attach JWT token to Authorization header if available
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Network tracking: record start time
  client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const requestId = crypto.randomUUID();
    config.metadata = { startTime: Date.now(), requestId };
    return config;
  });

  // Network tracking: record completed entries
  client.interceptors.response.use(
    (response) => {
      const duration = response.config.metadata
        ? Date.now() - response.config.metadata.startTime
        : null;
      pushNetworkEntry({
        method: (response.config.method ?? 'GET').toUpperCase(),
        url: response.config.url ?? '',
        baseURL: response.config.baseURL ?? null,
        resolvedUrl: resolveRequestUrl(response.config.baseURL, response.config.url),
        status: response.status,
        statusText: response.statusText,
        durationMs: duration,
        requestId: response.config.metadata?.requestId ?? null,
        responseRequestId: getHeaderValue(response.headers, 'x-request-id'),
        requestContentType: getHeaderValue(response.config.headers, 'Content-Type'),
        requestContentLength: estimateRequestBodySize(response.config),
        timeoutMs: typeof response.config.timeout === 'number' ? response.config.timeout : null,
        error: null,
        timestamp: new Date().toISOString(),
      });
      return response;
    },
    (error: AxiosError) => {
      const duration = error.config?.metadata
        ? Date.now() - error.config.metadata.startTime
        : null;
      const apiErrorBody = parseApiErrorBody(error.response?.data);
      pushNetworkEntry({
        method: (error.config?.method ?? 'GET').toUpperCase(),
        url: error.config?.url ?? '',
        baseURL: error.config?.baseURL ?? null,
        resolvedUrl: resolveRequestUrl(error.config?.baseURL, error.config?.url),
        status: error.response?.status ?? null,
        statusText: error.response?.statusText ?? null,
        durationMs: duration,
        requestId: error.config?.metadata?.requestId ?? null,
        responseRequestId: getHeaderValue(error.response?.headers, 'x-request-id'),
        requestContentType: getHeaderValue(error.config?.headers, 'Content-Type'),
        requestContentLength: error.config ? estimateRequestBodySize(error.config) : null,
        timeoutMs: typeof error.config?.timeout === 'number' ? error.config.timeout : null,
        error: error.message,
        errorName: error.name,
        errorCode: error.code ?? null,
        hasResponse: !!error.response,
        hasRequest: !!error.request,
        isTimeout: error.code === 'ECONNABORTED',
        responseErrorCode: apiErrorBody.code,
        responseErrorMessage: apiErrorBody.message,
        timestamp: new Date().toISOString(),
      });
      return Promise.reject(error);
    }
  );

  /**
   * Response Interceptor: Handle responses and errors
   */
  client.interceptors.response.use(
    (response) => {
      // Log successful response in development mode
      if (import.meta.env.DEV) {
        logger.log('APIClient', 'API Response', {
          status: response.status,
          url: response.config.url,
          data: response.data,
        });
      }

      return response;
    },
    async (error: AxiosError) => {
      // Log error response in development mode
      if (import.meta.env.DEV) {
        logger.error('APIClient', 'API Error', {
          status: error.response?.status,
          url: error.config?.url,
          data: error.response?.data,
        });
      }

      const originalRequest = error.config;

      // Handle 401 Unauthorized errors with token refresh
      if (
        error.response?.status === 401 &&
        originalRequest &&
        !originalRequest.url?.endsWith('/auth/refresh') &&
        !originalRequest.url?.endsWith('/auth/authenticate')
      ) {
        try {
          // If not already refreshing, start refresh
          if (!isRefreshing) {
            isRefreshing = true;

            // Create refresh promise
            refreshPromise = (async () => {
              try {
                // Import refreshAccessToken here to avoid circular dependency
                const { refreshAccessToken } = await import('./services/authApi');
                const { jwtToken, refreshToken } = await refreshAccessToken();

                // Save new tokens (token rotation)
                saveTokens(jwtToken, refreshToken);

                return jwtToken;
              } catch (refreshError) {
                // Refresh failed - clear tokens and redirect to login
                clearTokens();
                captureError('APIClient:401refresh', refreshError);

                // Dispatch custom event for logout
                window.dispatchEvent(new CustomEvent('auth:logout'));

                throw refreshError;
              } finally {
                isRefreshing = false;
                refreshPromise = null;
              }
            })();
          }

          // Wait for refresh to complete
          const newToken = await refreshPromise;

          // Retry original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }

          return client(originalRequest);
        } catch (error) {
          // If refresh failed, reject with original error
          const apiError = parseApiError(error);
          return Promise.reject(apiError);
        }
      }

      // Parse error and return rejection
      const apiError = parseApiError(error);
      return Promise.reject(apiError);
    }
  );

  return client;
};

// Create and export API client instance
export const apiClient = createApiClient();

export default apiClient;
